import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { AxiosError } from "axios";
import { IScore, IEvent, IDogClass, /*IShow, DogShowClassesMoveUpDto*/ } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";

import Button from "src/components/Button/Button";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import { toast } from "src/components/Toast/ToastManager";

import { ShowTypes, isUkcRallyShow, parseUkcRallyClass, ukcRallyClassLabel, ukcRallyLevelSections } from "@eagerdog/constants";

import "./MoveUp.scss";

interface IProps {
  event: IEvent | undefined,
  registration: IScore,
  onComplete(): void,
  onClose(): void
}

interface IDogClassMoveUp extends IDogClass {
  new_level?: string,
  new_section?: string
}

interface IScoreMoveUp extends IScore {
  dog_class: IDogClassMoveUp
}

interface IMoveUpShow {
  show_id: string,
  show_name: string,
  custom_show_name?: string,
  scores: IScoreMoveUp[]
}

interface IElementOptions { 
  [key: string]: IOption[];
}

const MoveUp: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);

  const [moveUpShows, setMoveUpShows] = useState<IMoveUpShow[]>([]);
  const [showOptions, setShowOptions] = useState<IElementOptions>();
  const [sectionOptions, setSectionOptions] = useState<IElementOptions>({});
  const isUkcRally = isUkcRallyShow(props.registration.sanctioning_club, props.registration.show_type);

  const findNextLevel = useCallback((currentLevel: string, currentElement: string, elementOptions?: IElementOptions) => {
    let options:IElementOptions = elementOptions ? elementOptions : showOptions || {};

    if (options[currentElement]) {
      for (let i in options[currentElement]) {
        if (options[currentElement][i].id === currentLevel) {
          let index:number = options[currentElement].indexOf(options[currentElement][i]);
          return options[currentElement][index].id;
        }
      }
    }

    return currentLevel;
  }, [showOptions]);

  const populateOptions = useCallback(() => {
    let options:IElementOptions = {};
    let sections:IElementOptions = {};

    let _showElements:any = ShowTypes[props.registration.sanctioning_club][props.registration.show_type];

    for (let se in _showElements) {
      const elementValue = _showElements[se];
      
      // Handle empty array case (unlikely for move-ups, but keeping for safety)
      if (Array.isArray(elementValue) && elementValue.length === 0) {
        if (options[props.registration.show_type] === undefined) {
          options[props.registration.show_type] = [];
        }

        options[props.registration.show_type].push({
          id: se,
          value: se
        });
      } 
      // Handle new config object structure with levels/sections
      else if (elementValue && typeof elementValue === 'object' && 'levels' in elementValue && Array.isArray(elementValue.levels)) {
        if (options[se] === undefined) {
          options[se] = [];
        }

        // Iterate over the levels array
        elementValue.levels.forEach((level: string) => {
          options[se].push({
            id: level,
            value: level
          });
        });

        if (Array.isArray(elementValue.sections) && elementValue.sections.length > 0) {
          sections[se] = elementValue.sections.map((section: string) => ({ id: section, value: section }));
        }
      }
      // Handle old structure: direct array of level strings
      else if (Array.isArray(elementValue)) {
        if (options[se] === undefined) {
          options[se] = [];
        }

        elementValue.forEach((level: string) => {
          options[se].push({
            id: level,
            value: level
          });
        });
      }
    }

    setShowOptions(options);
    setSectionOptions(sections);

    return options;
  }, [props.registration.show_type, props.registration.sanctioning_club]);

  const handleNewLevelChange = (value: IOption, scoreId: string) => {
    let _moveUpShows = JSON.parse(JSON.stringify(moveUpShows));

    for (let s in _moveUpShows) {
      let findScore = _moveUpShows[s].scores.filter((score: IScoreMoveUp, index: number) => { return score._id === scoreId; });

      if (findScore.length > 0) {
        let index:number = _moveUpShows[s].scores.indexOf(findScore[0]);
        _moveUpShows[s].scores[index].dog_class.new_level = value.id;
      }
    }

    setMoveUpShows(_moveUpShows);
  }

  const handleSectionChange = (value: IOption, scoreId: string) => {
    let _moveUpShows = JSON.parse(JSON.stringify(moveUpShows));

    for (let s in _moveUpShows) {
      const idx = _moveUpShows[s].scores.findIndex((score: IScoreMoveUp) => score._id === scoreId);
      if (idx !== -1) {
        _moveUpShows[s].scores[idx].dog_class.section = value.id;
      }
    }

    setMoveUpShows(_moveUpShows);
  };

  const handleNewSectionChange = (value: IOption, scoreId: string) => {
    let _moveUpShows = JSON.parse(JSON.stringify(moveUpShows));

    for (let s in _moveUpShows) {
      const idx = _moveUpShows[s].scores.findIndex((score: IScoreMoveUp) => score._id === scoreId);
      if (idx !== -1) {
        _moveUpShows[s].scores[idx].dog_class.new_section = value.id;
      }
    }

    setMoveUpShows(_moveUpShows);
  };

  const submitMoveUp = () => {
    if (props.event) {
      let _payload:any = {
        dog_classes: []
      }

      let _moveUpShows = JSON.parse(JSON.stringify(moveUpShows));

      for (let show in _moveUpShows) {
        for (let score in _moveUpShows[show].scores) {
          let _old_dog_class: any = _moveUpShows[show].scores[score].dog_class;

          // Only include classes where the user explicitly changed the level or section
          // (new_level / new_section are only set when user changes the dropdown)
          // UKC Rally: a class saved as "RO1 B" carries its section in the level; keep that
          // section when only the level changes so the target class stays complete.
          const currentSection = isUkcRally
            ? (parseUkcRallyClass(_old_dog_class.level, _old_dog_class.section).section || undefined)
            : _old_dog_class.section;
          const sectionChanged = !!_old_dog_class.new_section && _old_dog_class.new_section !== currentSection;
          if (_old_dog_class.new_level || sectionChanged) {
            let _dog_class:any = {
              score_id: _moveUpShows[show].scores[score]._id,
              dog_class: {
                section: sectionChanged ? _old_dog_class.new_section : currentSection,
                show_element: _old_dog_class.show_element,
                level: _old_dog_class.new_level || _old_dog_class.level
              }
            };

            _payload.dog_classes.push(_dog_class);
          }
        }
      }

      _payload.dog_classes = _payload.dog_classes.reverse();

      apiService.dogClassMoveUp(props.event._id, _payload).then((response) => {
        if (response === undefined) {
          toast.show({
            title: "Move Up",
            content: "Move up for " + props.registration.call_name + " successful",
            duration: 10000,
            type: "success"
          });

          props.onClose();
        }
      }).catch((e: any) => {
        toast.show({
          title: "Move up",
          content: e.response?.data?.message ? e.response.data.message : "Failed to move up " + props.registration.call_name,
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      });

    }
  }

  const getUkcScoreResult= (score: IScore) => {
    switch (score.show_type) {
      case Constants.show_type.nosework:
        return score.nosework;
      // conformation has no results yet
      // case Constants.show_type.conformation:
      //   break;
      case Constants.show_type.obedience:
        return score.obedience;
      case Constants.show_type.rally_obedience:
        return score.rally_obedience;
      case Constants.show_type.agility:
        return score.agility;
      case Constants.show_type.weight_pull:
        return score.weight_pull;
      case Constants.show_type.precision_coursing:
        return score.precision_coursing;
      case Constants.show_type.shed_dog:
        return score.shed_dog;
      case Constants.show_type.dock_jumping:
        return score.dock_jumping;
      default:
        break;
    }
  }

  useEffect(() => {
    if (!loaded) {
    let showOptions:IElementOptions = populateOptions();

      let _query:any = {
        query: {
          "$and": [{
            attribute_name: "dog_id",
            attribute_value: {
              operator: "$eq",
              value: props.registration.dog_id
            }
          }, {
            attribute_name: "dog_class.show_element",
            attribute_value: {
              operator: "$eq",
              value: props.registration.dog_class?.show_element
            }
          }, {
            attribute_name: "_id",
            attribute_value: {
              operator: "$ne",
              value: props.registration._id
            }
        }]
        },
        sort: []
      };

      apiService.getEventScores(props.registration.event_id, _query).then((response) => {
        let _dc:IMoveUpShow[] = [];

        for (let s in response) {
          let hasShow = _dc.filter((dc: any) => dc.show_id === response[s].show_id);
          let _show:any = JSON.parse(JSON.stringify(response[s]));
          // Don't automatically set new_level - only set it when user makes a change
          // _show.dog_class.new_level = findNextLevel(_show.dog_class.level, _show.dog_class.show_element, showOptions);

          if (hasShow.length === 0) {
            _dc.push({
              show_id: _show.show_id,
              show_name: _show.show_type + " " + _show.show_name + " (" + moment(_show.show_date).format("MMM Do") + ")",
              scores: [_show]
            });
          } else {
            hasShow[0].scores.push(_show);
          }
        }

        setMoveUpShows(_dc);
      }).catch((e: AxiosError) => {
        toast.show({
          title: "Get Dog Classes",
          content: "Failed to get Dog Classes",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      });
      
      setLoaded(true);
    }
  }, [loaded, props.registration, findNextLevel, populateOptions]);

  const renderScore = (score:IScoreMoveUp, index:number) => {
    // UKC Rally: sections depend on the level (RO1 A/B/C, RO2-ROM A/B, URX/RMX none) and a
    // class saved as "RO1 B" carries its section in the level.
    const rallyTargetLevel = score.dog_class?.new_level || score.dog_class?.level;
    const rallySections = isUkcRally ? ukcRallyLevelSections(rallyTargetLevel) : undefined;
    const elementSections = isUkcRally
      ? (rallySections && rallySections.length > 0 ? rallySections.map((s) => ({ id: s, value: s })) : undefined)
      : (score.dog_class ? sectionOptions[score.dog_class.show_element] : undefined);
    const currentSection = isUkcRally
      ? (parseUkcRallyClass(score.dog_class?.level, score.dog_class?.section).section || undefined)
      : (score.dog_class?.section && score.dog_class.section !== Constants.dog_class_section.none
        ? score.dog_class.section
        : undefined);
    const currentLabel = isUkcRally
      ? ukcRallyClassLabel(score.dog_class?.level, score.dog_class?.section)
      : (currentSection && elementSections
        ? `${score.dog_class.level} - ${currentSection}`
        : score.dog_class?.level);
    const newSection = score.dog_class?.new_section ?? currentSection ?? "";

    return (
      <div key={index + "score"} className="scoreWrap">
        {props.event?.sanctioning_club === Constants.sanctioning_club.UKC && !!getUkcScoreResult(score) && <div className="warningWrap">
          {`There are already results entered for the class (${score.dog_class.level}) you are trying to change.
          If you continue, the results will need to be re-entered.`}
        </div>}
        {score.dog_class && score.dog_class.level &&
          <Dropdown
            value={{id: score.dog_class.level, value: currentLabel || score.dog_class.level}}
            label={"Current Element (" + score.dog_class.show_element + ")"}
            disabled={true}
            onChange={(e, value: IOption) => { }}
            options={[{id: score.dog_class.level, value: currentLabel || score.dog_class.level}]}
            placeholder=""
          />
        }
        <div className="arrowWrap"><div className="icon arrowLeft"></div></div>
        {score.dog_class && score.dog_class.level && 
          <Dropdown 
            value={{id: score.dog_class.new_level || "", value: score.dog_class.new_level || ""}} 
            label={"New Element (" + score.dog_class.show_element + ")"} 
            onChange={(e, value: IOption) => {
              handleNewLevelChange(value, score._id);
            }} 
            options={showOptions && showOptions[score.dog_class.show_element] ? showOptions[score.dog_class.show_element] : []} 
            placeholder="" 
          />
        }
        {score.dog_class && score.dog_class.level && elementSections &&
          <Dropdown
            className="section"
            value={{ id: newSection, value: newSection }}
            label="Section"
            onChange={(e, value: IOption) => handleNewSectionChange(value, score._id)}
            options={elementSections}
            placeholder=""
          />
        }
        {(() => {
          const isUkcNosework =
            props.registration.sanctioning_club === Constants.sanctioning_club.UKC &&
            props.registration.show_type === Constants.show_type.nosework;
          if (!isUkcNosework) return null;

          const sectionOptions: IOption[] = [
            { id: "A", value: "A" },
            { id: "B", value: "B" },
          ];

          return (
            <Dropdown
              className="section"
              value={{ id: score.dog_class.section ?? "", value: score.dog_class.section ?? "" }}
              label="Section"
              onChange={(e, value: IOption) => handleSectionChange(value, score._id)}
              options={sectionOptions}
              placeholder=""
            />
          );
        })()}
      </div>
      );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); submitMoveUp(); }} className="MoveUp">
      <div className="title">
        <span>Move Up</span>
        <span>{props.registration.call_name} #{props.registration.arm_number}</span>
      </div>
      <div className="moveUpContent">
        {loaded && moveUpShows.length === 0 && <div className="empty">{props.registration.call_name} is not in any other shows</div>}
        {moveUpShows.map((show:IMoveUpShow, index:number) => {
          return (
            <div key={index + "show"} className="showWrap">
              <div className="showTitle">{show.custom_show_name || show.show_name}</div>
              {show.scores.map((score:IScoreMoveUp, indext:number) => {
                return(renderScore(score, indext));
              })}
            </div>
          );
        })}
      </div>
      <div className="actions">
        <Button onClick={() => { props.onClose(); }} type="button">Cancel</Button>
        {loaded && moveUpShows.length !== 0 && <Button type="submit">Confirm Changes</Button>}
      </div>
    </form>
  );
};

export default MoveUp;